import { useEffect, useId, useMemo, useRef, useState } from "react";
import { fuzzyFilter } from "../lib/fuzzy";

export interface Option { value: string; label: string; hint?: string }
const NEW = "\u0000new";   // the "create …" row's value

// Type to filter, arrows to move, Enter / Tab to pick. Keeps Enter for itself only while the list is open.
// With onCreate, typed text that matches no option offers "+ Create <noun> “text”" at the end of the list.
export function Combobox({ options, value, onChange, placeholder, disabled, autoFocus, id, allowEmpty, emptyLabel = "—", onCreate, createNoun }: {
  options: Option[]; value: string | null; onChange: (v: string | null) => void; placeholder?: string; disabled?: boolean;
  autoFocus?: boolean; id?: string; allowEmpty?: boolean; emptyLabel?: string;
  onCreate?: (text: string) => void; createNoun?: string;
}) {
  const current = options.find((o) => o.value === value);
  const [text, setText] = useState(current?.label ?? "");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { if (!open) setText(current?.label ?? ""); }, [current?.label, open]);

  const all = useMemo(() => (allowEmpty ? [{ value: "", label: emptyLabel }, ...options] : options), [options, allowEmpty, emptyLabel]);
  const typed = open && text !== (current?.label ?? "");
  const list = useMemo(() => {
    const found = typed ? fuzzyFilter(all, text, (o) => `${o.label} ${o.hint ?? ""}`, 50) : all.slice(0, 200);
    const t = text.trim();
    const exact = t && options.some((o) => o.label.trim().toLowerCase() === t.toLowerCase());
    return onCreate && open && (typed || !options.length) && !exact
      ? [...found, { value: NEW, label: t ? `+ Create ${createNoun ?? "new"} “${t}”` : `+ Create ${createNoun ?? "new"}`, hint: "" }]
      : found;
  }, [all, text, open, typed, onCreate, createNoun, options]);

  const pick = (o: Option | undefined) => {
    if (!o) return;
    if (o.value === NEW) { setOpen(false); setText(current?.label ?? ""); onCreate?.(text.trim() === (current?.label ?? "") ? "" : text.trim()); return; }
    onChange(o.value === "" ? null : o.value);
    setText(o.label === emptyLabel && o.value === "" ? "" : o.label);
    setOpen(false);
  };

  return (
    <div className="combo">
      <input ref={input} id={id} value={text} placeholder={placeholder ?? emptyLabel} disabled={disabled} autoFocus={autoFocus}
        role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list" autoComplete="off"
        onFocus={(e) => { e.target.select(); }}
        onChange={(e) => {   // retyping the chosen name keeps it chosen (the full list shows, with it selected)
          const v = e.target.value; setText(v); setOpen(true);
          setSel(v === (current?.label ?? "") ? Math.max(0, all.findIndex((o) => o.value === (value ?? ""))) : 0); }}
        onBlur={() => { setTimeout(() => setOpen(false), 120); }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); if (!open) { setOpen(true); setSel(Math.max(0, all.findIndex((o) => o.value === (value ?? "")))); } else setSel((s) => Math.min(s + 1, list.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
          else if ((e.key === "Enter" || e.key === "Tab") && open) { if (e.key === "Enter") e.preventDefault(); pick(list[sel]); }
          else if (e.key === "Escape" && open) { e.preventDefault(); e.stopPropagation(); setOpen(false); setText(current?.label ?? ""); }
        }} />
      {open && list.length > 0 && (
        <ul className="combo-list" id={listId} role="listbox">
          {list.map((o, i) => (
            <li key={o.value || "_empty"} role="option" aria-selected={i === sel} className={`${i === sel ? "sel" : ""}${o.value === NEW ? " combo-new" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); pick(o); }} onMouseEnter={() => setSel(i)}>
              <span>{o.label}</span>{o.hint && <span className="muted small">{o.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
